import { useInfiniteQuery } from '@tanstack/react-query';
import { getFeatureAnnouncements } from 'api/services';
import { Button } from 'library/buttons';
import { CircularProgress } from 'library/loaders';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FeatureGuideline from '../components/FeatureGuideline';
import { getFeatureGuideline, hasFeatureGuideline } from '../feature-announcement.utils';

const FeatureGuidePage = () => {
    const { featureId } = useParams();
    const navigate = useNavigate();
    const releases = useInfiniteQuery(
        ['featureAnnouncements', 'archive'],
        ({ pageParam = 0 }) => getFeatureAnnouncements({ offset: pageParam, limit: 20 }),
        {
            getNextPageParam: (lastPage, pages) => {
                const count = pages.reduce((total, page) => total + page.items.length, 0);
                return count < lastPage.total ? count : undefined;
            },
            staleTime: 10 * 60 * 1000,
        }
    );
    const feature = releases.data?.pages
        .flatMap((page) => page.items)
        .flatMap((announcement) => announcement.features ?? [])
        .find((item) => item._id === featureId);
    useEffect(() => {
        if (!feature && releases.hasNextPage && !releases.isFetchingNextPage && !releases.isError) {
            void releases.fetchNextPage();
        }
    }, [feature, releases]);
    const guideline = feature && getFeatureGuideline(feature);
    if (feature && hasFeatureGuideline(feature) && guideline) {
        return (
            <FeatureGuideline
                key={feature._id}
                feature={feature}
                guideline={guideline}
                onBack={() => navigate(`/whats-new?open=${encodeURIComponent(feature._id)}`)}
            />
        );
    }
    const loading = releases.isLoading || (!feature && releases.hasNextPage && !releases.isError);
    if (loading) {
        return (
            <main className="flex min-h-[60vh] w-full items-center justify-center text-primary" aria-busy="true">
                <CircularProgress size={32} aria-label="Loading feature guide" />
            </main>
        );
    }
    return (
        <main className="w-full p-6 md:p-10">
            <div className="mx-auto max-w-[1080px] rounded-2xl bg-card-color p-8">
                <p role="status">
                    {releases.isError ? 'Unable to load the feature guide.' : 'This feature guide is unavailable.'}
                </p>
                {releases.isError && (
                    <Button className="mt-4" size="sm" onClick={() => releases.refetch()}>
                        Try again
                    </Button>
                )}
                <Button className="mt-4" color="secondary" size="sm" onClick={() => navigate('/whats-new')}>
                    Back to What’s New
                </Button>
            </div>
        </main>
    );
};
export default FeatureGuidePage;
